/**
 * v4.6.1 — OperationalBrandHeader.
 *
 * Resolves the active brand identity for any native Operational Base
 * surface and renders it in one of two visual modes:
 *
 *   variant="inline" (default)
 *      A small horizontal strip: [logo box] [display_name + tagline].
 *      Used in tight contexts (future native surfaces, list pages).
 *      Sizes: sm / md / lg.
 *
 *   variant="showcase"
 *      A large CENTERED logo that dominates the page hero — exactly
 *      like an operator-facing franchise mark or org wordmark deserves.
 *      The logo image carries the brand identity on its own; no inline
 *      label/tagline is rendered. Honors "Invisible means non-existent":
 *      if there is no demo logo AND no real org logo, the entire block
 *      renders nothing — the page hero falls back to its plain title.
 *      Sizes: xl (256px) / hero (320px) / cinematic (400px).
 *
 * Resolution priority (unchanged from v4.5.0):
 *   1. Active Guided Demo → persona.logo_url
 *   2. Real organization brand → /api/operational-base/branding
 *   3. Calm fallback → nothing rendered, unless `requireFallback`.
 */
import React, { useEffect, useState } from "react";
import { useDemo } from "../demo/DemoContext";
import api from "../../lib/api";

export const OperationalBrandHeader = ({
    variant = "inline",         // "inline" | "showcase"
    size = "md",
    requireFallback = false,
}) => {
    const { isActive: demoActive, persona } = useDemo();
    const [branding, setBranding] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (demoActive) { setLoaded(true); return; }
        let cancelled = false;
        const fetchBranding = () => {
            api.get("/operational-base/branding")
                .then((res) => { if (!cancelled) setBranding(res.data); })
                .catch(() => { if (!cancelled) setBranding(null); })
                .finally(() => { if (!cancelled) setLoaded(true); });
        };
        fetchBranding();
        // Refetch when Demo Mode flips (so Blue Flame branding takes over
        // immediately, and the original brand returns when toggled off).
        const onDemoModeChange = () => { fetchBranding(); };
        window.addEventListener("ob:demo-mode-changed", onDemoModeChange);
        return () => {
            cancelled = true;
            window.removeEventListener("ob:demo-mode-changed", onDemoModeChange);
        };
    }, [demoActive]);

    // Resolve source.
    let logoUrl = null;
    let label = null;
    let tagline = null;
    let isFallback = false;

    if (demoActive && persona) {
        logoUrl = persona.logo_url || null;
        label = persona.display_name || null;
        tagline = persona.tagline || null;
    } else if (branding?.logo_url) {
        logoUrl = branding.logo_url;
        label = branding.organization?.display_name
            || branding.brand_name
            || branding.organization?.name
            || null;
    } else if (requireFallback) {
        logoUrl = "/yulontap-logo.png";
        isFallback = true;
    }

    if (!loaded) {
        if (!demoActive && !requireFallback) return null;
        return (
            <div
                aria-hidden="true"
                data-testid="ob-brand-header-loading"
                style={{ minHeight: variant === "showcase" ? 280 : 56 }}
            />
        );
    }

    if (!logoUrl && !label) return null;

    // ── Showcase variant — calm brand presence beside the page title. ───
    if (variant === "showcase") {
        const showcaseDims = {
            compact:    { logo: 180 },     // calm, left-aligned beside title
            xl:         { logo: 220 },
            hero:       { logo: 280 },
            cinematic:  { logo: 360 },
        }[size] || { logo: 200 };

        return (
            <div
                className="flex flex-shrink-0"
                data-testid="ob-brand-header"
                data-source={demoActive ? "demo" : isFallback ? "platform" : "organization"}
                data-variant="showcase"
            >
                {logoUrl && (
                    <img
                        src={logoUrl}
                        alt={label || "Brand"}
                        data-testid="ob-brand-header-logo"
                        style={{
                            height: showcaseDims.logo,
                            width: "auto",
                            maxWidth: "min(280px, 40vw)",
                            objectFit: "contain",
                            display: "block",
                            filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.45))",
                        }}
                    />
                )}
            </div>
        );
    }

    // ── Inline variant (sm/md/lg) — horizontal logo + label strip. ──
    const inlineDims = {
        sm: { h: 28, gap: 10, labelSize: "0.78rem", taglineSize: "0.62rem" },
        md: { h: 40, gap: 14, labelSize: "0.95rem", taglineSize: "0.68rem" },
        lg: { h: 56, gap: 18, labelSize: "1.05rem", taglineSize: "0.72rem" },
    }[size] || { h: 40, gap: 14, labelSize: "0.95rem", taglineSize: "0.68rem" };

    return (
        <div
            className="inline-flex items-center"
            data-testid="ob-brand-header"
            data-source={demoActive ? "demo" : isFallback ? "platform" : "organization"}
            data-variant="inline"
            style={{ gap: inlineDims.gap, marginBottom: 18 }}
        >
            {logoUrl && (
                <div
                    className="rounded-xl flex items-center justify-center"
                    style={{
                        height: inlineDims.h,
                        width: inlineDims.h,
                        padding: 6,
                        background: isFallback
                            ? "transparent"
                            : "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
                        border: isFallback
                            ? "none"
                            : "1px solid rgba(255,255,255,0.07)",
                        flexShrink: 0,
                    }}
                >
                    <img
                        src={logoUrl}
                        alt={label || "Logo"}
                        data-testid="ob-brand-header-logo"
                        style={{
                            maxHeight: "100%", maxWidth: "100%",
                            objectFit: "contain", display: "block",
                        }}
                    />
                </div>
            )}
            {label && (
                <div className="flex flex-col" style={{ minWidth: 0 }}>
                    <span
                        data-testid="ob-brand-header-label"
                        style={{
                            color: "var(--ob-text-primary)",
                            fontFamily: "Outfit, sans-serif",
                            fontSize: inlineDims.labelSize,
                            fontWeight: 500,
                            letterSpacing: "0.005em",
                            lineHeight: 1.2,
                        }}
                    >
                        {label}
                    </span>
                    {tagline && (
                        <span
                            data-testid="ob-brand-header-tagline"
                            className="uppercase"
                            style={{
                                color: "var(--ob-text-tertiary)",
                                fontSize: inlineDims.taglineSize,
                                letterSpacing: "0.18em",
                                marginTop: 3,
                            }}
                        >
                            {tagline}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default OperationalBrandHeader;
